import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Nav,
  Row,
  Spinner,
  Tab,
  Table,
} from "react-bootstrap";
import DataTable from "react-data-table-component";
import { AvForm, AvField } from "availity-reactstrap-validation";
import moment from "moment";
import { toast } from "react-toastify";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Header from "../../layouts/Header";
import DiscountSurfaces from "../../components/admin/DiscountSurfaces";
import Footer from "../../layouts/Footer";
import DataTableSkeleton from "../../components/DataTableSkeleton";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import AdminAddButton from "../../components/admin/AdminAddButton";
import ConfirmModal from "../../components/ConfirmModal";
import DashboardStatCard from "../../components/dashboard/DashboardStatCard";
import { getReferralDiscounts, normalizeReferralDiscountList } from "../../helper/referral_discount_helper";
import {
  CODE_SOURCES,
  OFFER_STATUSES,
  OFFER_TYPES,
  PROMO_DISCOUNT_TYPES,
  createPlatformPromoCode,
  createPromotionalOffer,
  deletePlatformPromoCode,
  deletePromotionalOffer,
  formatCurrency,
  formatDiscountDisplay,
  formatUsageDisplay,
  getOfferAnalytics,
  getOfferStatusBadge,
  getPlatformPromoCodes,
  getPromotionalOfferDetails,
  getPromotionalOffers,
  getRowId,
  normalizeOfferAnalytics,
  normalizeOfferDetail,
  normalizeOfferList,
  normalizeOfferPagination,
  normalizePromoCodeDetail,
  normalizePromoCodeList,
  updatePlatformPromoCode,
  updatePromotionalOffer,
  updatePromotionalOfferStatus,
} from "../../helper/offer_helper";
import { dataTableCustomStyles, dataTablePaginationOptions } from "../../components/admin/dataTableConfig";
import AdminRowActions, { AdminActionButton } from '../../components/admin/AdminRowActions';

const API_NOT_DEPLOYED_MSG =
  "This discounts list returned 404. The route is not on the API server this admin app is using.";

const safeText = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
};

const toDateInput = (value) => (value ? moment(value).format("YYYY-MM-DD") : "");

const buildPromoCreatePayload = (values) => {
  const payload = {
    code: String(values.code || "").trim().toUpperCase(),
    discountType: values.discountType,
    discountValue:
      values.discountType === "free_shipping" ? 0 : Number(values.discountValue),
    maxUsagePerCustomer: Number(values.maxUsagePerCustomer) || 1,
    firstPurchaseOnly: Boolean(values.firstPurchaseOnly),
    status: values.status || "active",
  };

  if (values.maxUsage !== "" && values.maxUsage != null) {
    payload.maxUsage = Number(values.maxUsage);
  } else {
    payload.maxUsage = null;
  }

  if (values.minOrderAmount !== "" && values.minOrderAmount != null) {
    payload.minOrderAmount = Number(values.minOrderAmount);
  } else {
    payload.minOrderAmount = null;
  }

  payload.startDate = values.startDate ? new Date(values.startDate).toISOString() : null;
  payload.endDate = values.endDate ? new Date(values.endDate).toISOString() : null;

  return payload;
};

const buildPromoUpdatePayload = (values) => {
  const payload = {
    discountType: values.discountType,
    discountValue:
      values.discountType === "free_shipping" ? 0 : Number(values.discountValue),
    maxUsagePerCustomer: Number(values.maxUsagePerCustomer) || 1,
    firstPurchaseOnly: Boolean(values.firstPurchaseOnly),
    status: values.status || "active",
  };

  if (values.maxUsage !== "" && values.maxUsage != null) {
    payload.maxUsage = Number(values.maxUsage);
  } else {
    payload.maxUsage = null;
  }

  if (values.minOrderAmount !== "" && values.minOrderAmount != null) {
    payload.minOrderAmount = Number(values.minOrderAmount);
  } else {
    payload.minOrderAmount = null;
  }

  payload.startDate = values.startDate ? new Date(values.startDate).toISOString() : null;
  payload.endDate = values.endDate ? new Date(values.endDate).toISOString() : null;

  return payload;
};

const buildOfferPayload = (values) => {
  const payload = {
    title: String(values.title || "").trim(),
    description: String(values.description || "").trim(),
    bannerText: String(values.bannerText || "").trim(),
    code: String(values.code || "").trim().toUpperCase(),
    offerType: values.offerType || undefined,
    priority: Number(values.priority) || 0,
    status: values.status || "active",
    showCountdown: Boolean(values.showCountdown),
    badgeText: String(values.badgeText || "").trim(),
  };

  payload.startDate = values.startDate ? new Date(values.startDate).toISOString() : null;
  payload.endDate = values.endDate ? new Date(values.endDate).toISOString() : null;

  return payload;
};

function OfferAnalyticsPanel({ analytics, loading, error, onRefresh, dateFrom, dateTo, onDateFromChange, onDateToChange, onApplyDates }) {
  const currency = analytics?.summary?.currency || "INR";
  const summary = analytics?.summary || {};

  const monthlyChartData = useMemo(
    () =>
      (analytics?.monthlyReports || []).map((row) => ({
        label: `${row.month}/${row.year}`,
        usages: row.usages || 0,
        discountGiven: row.discountGiven || 0,
      })),
    [analytics?.monthlyReports]
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="text-muted small mt-2 mb-0">Loading analytics…</p>
      </div>
    );
  }

  if (error) {
    return (
      <AdminEmptyState
        icon="ri-bar-chart-line"
        title="Analytics unavailable"
        description={error}
        actionLabel="Retry"
        onAction={onRefresh}
      />
    );
  }

  return (
    <div>
      <Form
        className="d-flex flex-wrap gap-2 mb-3"
        onSubmit={(e) => {
          e.preventDefault();
          onApplyDates();
        }}
      >
        <Form.Control
          type="date"
          value={dateFrom}
          onChange={(e) => onDateFromChange(e.target.value)}
          style={{ width: "auto" }}
        />
        <Form.Control
          type="date"
          value={dateTo}
          onChange={(e) => onDateToChange(e.target.value)}
          style={{ width: "auto" }}
        />
        <Button type="submit" variant="outline-primary" size="sm">
          Apply date range
        </Button>
      </Form>

      <Row className="g-3 mb-4">
        <Col md={4} lg={2}>
          <DashboardStatCard label="Views" value={summary.totalViews ?? 0} icon="ri-eye-line" iconBg="#DBEAFE" iconColor="#2563EB" accentColor="#2563EB" />
        </Col>
        <Col md={4} lg={2}>
          <DashboardStatCard label="Clicks" value={summary.totalClicks ?? 0} icon="ri-cursor-line" iconBg="#E0E7FF" iconColor="#4F46E5" accentColor="#4F46E5" />
        </Col>
        <Col md={4} lg={2}>
          <DashboardStatCard label="Copies" value={summary.totalCopies ?? 0} icon="ri-file-copy-line" iconBg="#F3E8FF" iconColor="#9333EA" accentColor="#9333EA" />
        </Col>
        <Col md={4} lg={2}>
          <DashboardStatCard label="Coupon usage" value={summary.totalCouponUsage ?? 0} icon="ri-coupon-3-line" iconBg="#FEF3C7" iconColor="#D97706" accentColor="#D97706" />
        </Col>
        <Col md={4} lg={2}>
          <DashboardStatCard label="Discount given" value={formatCurrency(summary.totalDiscountGiven, currency)} icon="ri-percent-line" iconBg="#DCFCE7" iconColor="#16A34A" accentColor="#16A34A" />
        </Col>
        <Col md={4} lg={2}>
          <DashboardStatCard label="CTR" value={`${summary.clickThroughRate ?? 0}%`} icon="ri-line-chart-line" iconBg="#FEE2E2" iconColor="#DC2626" accentColor="#DC2626" />
        </Col>
      </Row>

      <Row className="g-3">
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom">
              <h6 className="mb-0">Top offers by usage</h6>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Code</th>
                    <th>Views</th>
                    <th>Usage</th>
                  </tr>
                </thead>
                <tbody>
                  {(analytics?.topOffers || []).length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted small py-4">
                        No offer usage yet
                      </td>
                    </tr>
                  ) : (
                    analytics.topOffers.map((row) => (
                      <tr key={row.id}>
                        <td>{safeText(row.title)}</td>
                        <td><strong>{safeText(row.code)}</strong></td>
                        <td>{row.viewCount ?? 0}</td>
                        <td>{row.usageCount ?? 0}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom">
              <h6 className="mb-0">All offers performance</h6>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>CTR</th>
                    <th>Conv.</th>
                    <th>Discount</th>
                  </tr>
                </thead>
                <tbody>
                  {(analytics?.offers || []).length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted small py-4">
                        No offers yet
                      </td>
                    </tr>
                  ) : (
                    analytics.offers.map((row) => (
                      <tr key={row.id}>
                        <td>{safeText(row.title)}</td>
                        <td>{row.clickThroughRate ?? 0}%</td>
                        <td>{row.conversionRate ?? 0}%</td>
                        <td>{formatCurrency(row.discountGiven, currency)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm mt-3">
        <Card.Header className="bg-white border-bottom">
          <h6 className="mb-0">Monthly trend (last 12 months)</h6>
        </Card.Header>
        <Card.Body>
          {monthlyChartData.length === 0 ? (
            <p className="text-muted small mb-0 text-center py-4">No monthly data yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="usages" name="Usages" fill="#2563EB" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default function Offers() {
  const [activeTab, setActiveTab] = useState("promo-codes");

  const [promoCodes, setPromoCodes] = useState([]);
  const [promoLoading, setPromoLoading] = useState(true);
  const [promoSubmitting, setPromoSubmitting] = useState(false);
  const [promoSearch, setPromoSearch] = useState("");
  const [promoPage, setPromoPage] = useState(1);
  const [promoPerPage] = useState(10);
  const [promoTotalRows, setPromoTotalRows] = useState(0);
  const [promoStatusFilter, setPromoStatusFilter] = useState("");
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [isPromoEdit, setIsPromoEdit] = useState(false);
  const [editPromo, setEditPromo] = useState(null);
  const [promoDiscountType, setPromoDiscountType] = useState("percentage");

  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(true);
  const [offerSubmitting, setOfferSubmitting] = useState(false);
  const [offerSearch, setOfferSearch] = useState("");
  const [offerPage, setOfferPage] = useState(1);
  const [offerPerPage] = useState(10);
  const [offerTotalRows, setOfferTotalRows] = useState(0);
  const [offerStatusFilter, setOfferStatusFilter] = useState("");
  const [offerCodeSourceFilter, setOfferCodeSourceFilter] = useState("");
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [isOfferEdit, setIsOfferEdit] = useState(false);
  const [editOffer, setEditOffer] = useState(null);
  const [offerBannerPreview, setOfferBannerPreview] = useState("");
  const [availableCodes, setAvailableCodes] = useState([]);

  const [viewOffer, setViewOffer] = useState(null);
  const [viewOfferLoading, setViewOfferLoading] = useState(false);

  const [confirm, setConfirm] = useState(false);
  const [currentData, setCurrentData] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [analyticsFrom, setAnalyticsFrom] = useState("");
  const [analyticsTo, setAnalyticsTo] = useState("");

  const fetchPromoCodes = async () => {
    setPromoLoading(true);
    try {
      const params = { page: promoPage, limit: promoPerPage };
      if (promoSearch.trim()) params.search = promoSearch.trim();
      if (promoStatusFilter) params.status = promoStatusFilter;

      const res = await getPlatformPromoCodes(params);
      const list = normalizePromoCodeList(res);
      const pagination = normalizeOfferPagination(res);

      if (res?.success === false && !list.length) {
        toast.error(res?.message || "Failed to load platform promo codes");
        setPromoCodes([]);
        setPromoTotalRows(0);
        return;
      }

      setPromoCodes(list);
      setPromoTotalRows(pagination?.total || list.length);
    } catch (error) {
      console.error("Failed to fetch platform promo codes:", error);
      if (error?.status === 404) {
        toast.error(API_NOT_DEPLOYED_MSG);
      } else {
        toast.error(error?.message || "Failed to load platform promo codes");
      }
      setPromoCodes([]);
      setPromoTotalRows(0);
    } finally {
      setPromoLoading(false);
    }
  };

  const fetchOffers = async () => {
    setOffersLoading(true);
    try {
      const params = { page: offerPage, limit: offerPerPage };
      if (offerSearch.trim()) params.search = offerSearch.trim();
      if (offerStatusFilter) params.status = offerStatusFilter;
      if (offerCodeSourceFilter) params.codeSource = offerCodeSourceFilter;

      const res = await getPromotionalOffers(params);
      const list = normalizeOfferList(res);
      const pagination = normalizeOfferPagination(res);

      if (res?.success === false && !list.length) {
        toast.error(res?.message || "Failed to load promotional offers");
        setOffers([]);
        setOfferTotalRows(0);
        return;
      }

      setOffers(list);
      setOfferTotalRows(pagination?.total || list.length);
    } catch (error) {
      console.error("Failed to fetch promotional offers:", error);
      if (error?.status === 404) {
        toast.error(API_NOT_DEPLOYED_MSG);
      } else {
        toast.error(error?.message || "Failed to load promotional offers");
      }
      setOffers([]);
      setOfferTotalRows(0);
    } finally {
      setOffersLoading(false);
    }
  };

  const fetchAvailableCodes = useCallback(async () => {
    try {
      const [promoRes, referralRes] = await Promise.all([
        getPlatformPromoCodes({ page: 1, limit: 200, status: "active" }).catch(() => null),
        getReferralDiscounts({ page: 1, limit: 200, status: "active" }).catch(() => null),
      ]);

      const promoList = promoRes ? normalizePromoCodeList(promoRes) : [];
      const referralList = referralRes ? normalizeReferralDiscountList(referralRes) : [];

      const codes = [
        ...promoList.map((row) => ({
          code: row.code,
          source: "platform",
          label: `${row.code} (platform)`,
        })),
        ...referralList.map((row) => ({
          code: row.referralCode,
          source: "referral",
          label: `${row.referralCode} (referral)`,
        })),
      ].filter((item) => item.code);

      setAvailableCodes(codes);
    } catch (error) {
      console.error("Failed to load available codes:", error);
      setAvailableCodes([]);
    }
  }, []);

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const params = {};
      if (analyticsFrom) params.from = analyticsFrom;
      if (analyticsTo) params.to = analyticsTo;

      const res = await getOfferAnalytics(params);
      if (res?.success === false) {
        throw new Error(res?.message || "Failed to load analytics");
      }
      setAnalytics(normalizeOfferAnalytics(res));
    } catch (error) {
      console.error("Failed to fetch offer analytics:", error);
      const message =
        error?.status === 404
          ? "Analytics API not deployed yet. Deploy backend with promotional offers module."
          : error?.message || "Failed to load analytics";
      setAnalyticsError(message);
      setAnalytics(normalizeOfferAnalytics(null));
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "promo-codes") {
      fetchPromoCodes();
    }
  }, [promoPage, promoStatusFilter, activeTab]);

  useEffect(() => {
    if (activeTab === "banner-offers") {
      fetchOffers();
      fetchAvailableCodes();
    }
  }, [offerPage, offerStatusFilter, offerCodeSourceFilter, activeTab, fetchAvailableCodes]);

  useEffect(() => {
    if (activeTab === "analytics") {
      fetchAnalytics();
    }
  }, [activeTab]);

  const handlePromoSearchSubmit = (e) => {
    e.preventDefault();
    setPromoPage(1);
    fetchPromoCodes();
  };

  const handleOfferSearchSubmit = (e) => {
    e.preventDefault();
    setOfferPage(1);
    fetchOffers();
  };

  const handlePromoFormSubmit = async (e, values) => {
    setPromoSubmitting(true);
    try {
      let res;

      if (isPromoEdit) {
        const id = getRowId(editPromo);
        if (!id) throw new Error("Promo code ID not found");
        res = await updatePlatformPromoCode(id, buildPromoUpdatePayload(values));
      } else {
        if (!String(values.code || "").trim()) {
          toast.error("Enter a promo code");
          setPromoSubmitting(false);
          return;
        }
        if (values.discountType !== "free_shipping") {
          const val = Number(values.discountValue);
          if (Number.isNaN(val) || val <= 0) {
            toast.error("Enter a valid discount value");
            setPromoSubmitting(false);
            return;
          }
        }
        res = await createPlatformPromoCode(buildPromoCreatePayload(values));
      }

      if (res?.success === false) {
        toast.error(res?.message || "Failed to save platform promo code");
        return;
      }

      toast.success(res?.message || (isPromoEdit ? "Promo code updated" : "Promo code created"));
      setShowPromoModal(false);
      setIsPromoEdit(false);
      setEditPromo(null);
      fetchPromoCodes();
    } catch (error) {
      console.error("Error saving promo code:", error);
      if (error?.status === 409) {
        toast.error(error?.message || "This promo code already exists");
      } else if (error?.status === 400) {
        toast.error(error?.message || "Invalid promo code data");
      } else {
        toast.error(error?.message || "Failed to save platform promo code");
      }
    } finally {
      setPromoSubmitting(false);
    }
  };

  const handleOfferFormSubmit = async (e, values) => {
    setOfferSubmitting(true);
    try {
      let res;
      const payload = buildOfferPayload(values);

      if (!payload.title || !payload.code) {
        toast.error("Title and code are required");
        setOfferSubmitting(false);
        return;
      }

      if (isOfferEdit) {
        const id = getRowId(editOffer);
        if (!id) throw new Error("Offer ID not found");
        res = await updatePromotionalOffer(id, payload);
      } else {
        res = await createPromotionalOffer(payload);
      }

      if (res?.success === false) {
        toast.error(res?.message || "Failed to save promotional offer");
        return;
      }

      toast.success(res?.message || (isOfferEdit ? "Offer updated" : "Offer created"));
      setShowOfferModal(false);
      setIsOfferEdit(false);
      setEditOffer(null);
      setOfferBannerPreview("");
      fetchOffers();
    } catch (error) {
      console.error("Error saving offer:", error);
      if (error?.status === 400) {
        toast.error(
          error?.message ||
            "Code not found. Create a platform promo code or referral discount code first."
        );
      } else {
        toast.error(error?.message || "Failed to save promotional offer");
      }
    } finally {
      setOfferSubmitting(false);
    }
  };

  const handleToggleOfferStatus = async (offer) => {
    const id = getRowId(offer);
    if (!id) return;

    const nextStatus = offer.status === "active" ? "inactive" : "active";
    setActionLoading(true);
    try {
      const res = await updatePromotionalOfferStatus(id, nextStatus);
      if (res?.success === false) {
        toast.error(res?.message || "Failed to update offer status");
        return;
      }
      toast.success(res?.message || `Offer ${nextStatus === "active" ? "activated" : "deactivated"}`);
      fetchOffers();
    } catch (error) {
      console.error("Toggle offer status failed:", error);
      toast.error(error?.message || "Failed to update offer status");
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!currentData?._id) return;

    setActionLoading(true);
    try {
      let res;
      if (currentData.entityType === "promo") {
        res = await deletePlatformPromoCode(currentData._id);
      } else {
        res = await deletePromotionalOffer(currentData._id);
      }

      if (res?.success === false) {
        toast.error(res?.message || "Failed to delete");
        return;
      }

      toast.success(res?.message || "Deleted successfully");
      setConfirm(false);
      setCurrentData(null);

      if (currentData.entityType === "promo") {
        fetchPromoCodes();
      } else {
        fetchOffers();
      }
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error(error?.message || "Failed to delete");
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewOffer = async (offer) => {
    const id = getRowId(offer);
    if (!id) {
      toast.error("Offer ID not found");
      return;
    }

    setViewOffer(offer);
    setViewOfferLoading(true);
    try {
      const res = await getPromotionalOfferDetails(id);
      const detail = normalizeOfferDetail(res);
      if (detail && typeof detail === "object") {
        setViewOffer(detail);
      }
    } catch (error) {
      console.error("Failed to load offer details:", error);
      toast.error(error?.message || "Failed to load offer details");
    } finally {
      setViewOfferLoading(false);
    }
  };

  const promoColumns = [
    {
      name: "Code",
      selector: (row) => row.code || "",
      sortable: true,
      minWidth: "120px",
      cell: (row) => <strong>{safeText(row.code)}</strong>,
    },
    {
      name: "Discount",
      minWidth: "130px",
      cell: (row) => formatDiscountDisplay(row.discountType, row.discountValue),
    },
    {
      name: "Status",
      minWidth: "110px",
      cell: (row) => (
        <Badge bg={getOfferStatusBadge(row.status)}>
          {row.status === "active" ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      name: "Usage",
      minWidth: "110px",
      center: true,
      cell: (row) => formatUsageDisplay(row.currentUsage, row.maxUsage),
    },
    {
      name: "First purchase",
      minWidth: "120px",
      center: true,
      cell: (row) => (row.firstPurchaseOnly ? "Yes" : "No"),
    },
    {
      name: "Schedule",
      minWidth: "180px",
      cell: (row) => {
        const start = row.startDate ? moment(row.startDate).format("DD MMM YYYY") : "—";
        const end = row.endDate ? moment(row.endDate).format("DD MMM YYYY") : "—";
        return <span className="small">{start} → {end}</span>;
      },
    },
    {
      name: "Actions",
      minWidth: "130px",
      center: true,
      cell: (row) => (
        <AdminRowActions>
          <AdminActionButton
            action="edit"
            onClick={() => {
              setEditPromo(row);
              setIsPromoEdit(true);
              setPromoDiscountType(row.discountType || "percentage");
              setShowPromoModal(true);
            }}
          />
          {row.status === "active" && (
            <AdminActionButton
              action="delete"
              onClick={() => {
                setCurrentData({
                  _id: getRowId(row),
                  entityType: "promo",
                  actionType: "Delete",
                  message: `Delete platform promo code "${row.code}"? Linked banner offers may fail validation.`,
                });
                setConfirm(true);
              }}
            />
          )}
        </AdminRowActions>
      ),
      ignoreRowClick: true,
    },
  ];

  const offerColumns = [
    {
      name: "Title",
      selector: (row) => row.title || "",
      sortable: true,
      minWidth: "150px",
      cell: (row) => <strong>{safeText(row.title)}</strong>,
    },
    {
      name: "Banner preview",
      minWidth: "220px",
      cell: (row) => (
        <span className="small text-truncate d-inline-block" style={{ maxWidth: "200px" }} title={row.bannerText}>
          {safeText(row.bannerText)}
        </span>
      ),
    },
    {
      name: "Code",
      minWidth: "110px",
      cell: (row) => <strong>{safeText(row.code)}</strong>,
    },
    {
      name: "Source",
      minWidth: "100px",
      cell: (row) => (
        <Badge bg={row.codeSource === "referral" ? "info" : "primary"}>
          {row.codeSource === "referral" ? "Referral" : "Platform"}
        </Badge>
      ),
    },
    {
      name: "Priority",
      selector: (row) => row.priority ?? 0,
      sortable: true,
      minWidth: "90px",
      center: true,
    },
    {
      name: "Status",
      minWidth: "110px",
      cell: (row) => (
        <Badge bg={getOfferStatusBadge(row.status)}>
          {row.status === "active" ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      name: "Engagement",
      minWidth: "120px",
      cell: (row) => (
        <span className="small">
          {row.viewCount ?? 0}v / {row.clickCount ?? 0}c
        </span>
      ),
    },
    {
      name: "Actions",
      minWidth: "180px",
      center: true,
      cell: (row) => (
        <AdminRowActions>
          <AdminActionButton action="view" onClick={() => handleViewOffer(row)} />
          <AdminActionButton
            action="edit"
            onClick={() => {
              setEditOffer(row);
              setIsOfferEdit(true);
              setOfferBannerPreview(row.bannerText || "");
              setShowOfferModal(true);
            }}
          />
          <AdminActionButton
            icon={row.status === "active" ? "ri-pause-circle-line" : "ri-play-circle-line"}
            label={row.status === "active" ? "Deactivate" : "Activate"}
            tone={row.status === "active" ? undefined : "success"}
            onClick={() => handleToggleOfferStatus(row)}
            disabled={actionLoading}
          />
          <AdminActionButton
            action="delete"
            onClick={() => {
              setCurrentData({
                _id: getRowId(row),
                entityType: "offer",
                actionType: "Delete",
                message: `Delete banner offer "${row.title}"?`,
              });
              setConfirm(true);
            }}
          />
        </AdminRowActions>
      ),
      ignoreRowClick: true,
    },
  ];

  const handleRefresh = () => {
    if (activeTab === "promo-codes") fetchPromoCodes();
    else if (activeTab === "banner-offers") fetchOffers();
    else fetchAnalytics();
  };

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <DiscountSurfaces />
        <div className="d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/admin/dashboard">Dashboard</Link></li>
              <li className="breadcrumb-item active" aria-current="page">Offers</li>
            </ol>
            <h4 className="main-title mb-0 admin-page-title">Offer Management</h4>
            <p className="text-secondary small mb-0 mt-1">
              Platform promo codes (discount logic) and promotional banner campaigns
            </p>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <Button
              variant="outline-secondary"
              onClick={handleRefresh}
              disabled={promoLoading || offersLoading || analyticsLoading}
            >
              <i className={`ri-refresh-line me-1 ${promoLoading || offersLoading || analyticsLoading ? "spin" : ""}`} />
              Refresh
            </Button>
            {activeTab === "promo-codes" && (
              <AdminAddButton
                label="Add Promo Code"
                onClick={() => {
                  setEditPromo(null);
                  setIsPromoEdit(false);
                  setPromoDiscountType("percentage");
                  setShowPromoModal(true);
                }}
              />
            )}
            {activeTab === "banner-offers" && (
              <AdminAddButton
                label="Add Banner Offer"
                onClick={() => {
                  setEditOffer(null);
                  setIsOfferEdit(false);
                  setOfferBannerPreview("");
                  setShowOfferModal(true);
                  fetchAvailableCodes();
                }}
              />
            )}
          </div>
        </div>

        <Tab.Container activeKey={activeTab} onSelect={(key) => key && setActiveTab(key)}>
          <Nav variant="tabs" className="mb-3">
            <Nav.Item>
              <Nav.Link eventKey="promo-codes">
                <i className="ri-coupon-3-line me-1" />
                Promo Codes
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="banner-offers">
                <i className="ri-gift-line me-1" />
                Banner Offers
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="analytics">
                <i className="ri-bar-chart-line me-1" />
                Analytics
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            <Tab.Pane eventKey="promo-codes">
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <Alert variant="info" className="small py-2">
                    Step 1: Create platform promo codes here (e.g. WELCOME10, FREESHIP). Banner offers in the next tab must reference an existing platform or referral code.
                  </Alert>
                  <Form onSubmit={handlePromoSearchSubmit} className="d-flex flex-wrap gap-2 mb-3">
                    <Form.Control
                      type="search"
                      placeholder="Search by code…"
                      value={promoSearch}
                      onChange={(e) => setPromoSearch(e.target.value)}
                      style={{ minWidth: "220px", maxWidth: "360px" }}
                    />
                    <Form.Select
                      className="w-auto"
                      value={promoStatusFilter}
                      onChange={(e) => {
                        setPromoStatusFilter(e.target.value);
                        setPromoPage(1);
                      }}
                    >
                      <option value="">All status</option>
                      {OFFER_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </Form.Select>
                    <Button type="submit" variant="primary" disabled={promoLoading}>Search</Button>
                  </Form>

                  <div className="admin-datatable-scroll">
                    <DataTable
                      columns={promoColumns}
                      data={promoCodes}
                      pagination
                      paginationServer
                      paginationTotalRows={promoTotalRows}
                      paginationDefaultPage={promoPage}
                      onChangePage={(newPage) => setPromoPage(newPage)}
                      paginationPerPage={promoPerPage}
                      paginationComponentOptions={dataTablePaginationOptions}
                      progressPending={promoLoading}
                      progressComponent={<DataTableSkeleton rows={6} columns={7} />}
                      customStyles={dataTableCustomStyles}
                      striped
                      highlightOnHover
                      noDataComponent={
                        <AdminEmptyState
                          icon="ri-coupon-3-line"
                          title="No platform promo codes"
                          description="Create WELCOME10 or other platform-wide discount codes before adding banner offers."
                          actionLabel="Add Promo Code"
                          onAction={() => {
                            setEditPromo(null);
                            setIsPromoEdit(false);
                            setShowPromoModal(true);
                          }}
                        />
                      }
                    />
                  </div>
                </Card.Body>
              </Card>
            </Tab.Pane>

            <Tab.Pane eventKey="banner-offers">
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <Alert variant="warning" className="small py-2">
                    Step 2: Link a banner to an existing code. If the code is missing, the API returns: &quot;Code not found. Create a platform promo code or referral discount code first.&quot;
                  </Alert>
                  <Form onSubmit={handleOfferSearchSubmit} className="d-flex flex-wrap gap-2 mb-3">
                    <Form.Control
                      type="search"
                      placeholder="Search title or code…"
                      value={offerSearch}
                      onChange={(e) => setOfferSearch(e.target.value)}
                      style={{ minWidth: "220px", maxWidth: "360px" }}
                    />
                    <Form.Select
                      className="w-auto"
                      value={offerStatusFilter}
                      onChange={(e) => {
                        setOfferStatusFilter(e.target.value);
                        setOfferPage(1);
                      }}
                    >
                      <option value="">All status</option>
                      {OFFER_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </Form.Select>
                    <Form.Select
                      className="w-auto"
                      value={offerCodeSourceFilter}
                      onChange={(e) => {
                        setOfferCodeSourceFilter(e.target.value);
                        setOfferPage(1);
                      }}
                    >
                      <option value="">All sources</option>
                      {CODE_SOURCES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </Form.Select>
                    <Button type="submit" variant="primary" disabled={offersLoading}>Search</Button>
                  </Form>

                  <div className="admin-datatable-scroll">
                    <DataTable
                      columns={offerColumns}
                      data={offers}
                      pagination
                      paginationServer
                      paginationTotalRows={offerTotalRows}
                      paginationDefaultPage={offerPage}
                      onChangePage={(newPage) => setOfferPage(newPage)}
                      paginationPerPage={offerPerPage}
                      paginationComponentOptions={dataTablePaginationOptions}
                      progressPending={offersLoading}
                      progressComponent={<DataTableSkeleton rows={6} columns={8} />}
                      customStyles={dataTableCustomStyles}
                      striped
                      highlightOnHover
                      noDataComponent={
                        <AdminEmptyState
                          icon="ri-gift-line"
                          title="No banner offers"
                          description="Create a platform promo code first, then add a promotional banner campaign."
                          actionLabel="Add Banner Offer"
                          onAction={() => {
                            setEditOffer(null);
                            setIsOfferEdit(false);
                            setShowOfferModal(true);
                            fetchAvailableCodes();
                          }}
                        />
                      }
                    />
                  </div>
                </Card.Body>
              </Card>
            </Tab.Pane>

            <Tab.Pane eventKey="analytics">
              <OfferAnalyticsPanel
                analytics={analytics}
                loading={analyticsLoading}
                error={analyticsError}
                onRefresh={fetchAnalytics}
                dateFrom={analyticsFrom}
                dateTo={analyticsTo}
                onDateFromChange={setAnalyticsFrom}
                onDateToChange={setAnalyticsTo}
                onApplyDates={fetchAnalytics}
              />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        <Modal show={showPromoModal} onHide={() => setShowPromoModal(false)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{isPromoEdit ? "Edit Platform Promo Code" : "Create Platform Promo Code"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AvForm onValidSubmit={handlePromoFormSubmit}>
              {!isPromoEdit && (
                <AvField
                  name="code"
                  label="Code *"
                  type="text"
                  placeholder="e.g. WELCOME10"
                  disabled={promoSubmitting}
                  style={{ textTransform: "uppercase" }}
                  validate={{
                    required: { value: true, errorMessage: "Code is required" },
                    pattern: {
                      value: /^[A-Za-z0-9_-]+$/,
                      errorMessage: "Use letters, numbers, hyphen or underscore only",
                    },
                  }}
                />
              )}
              {isPromoEdit && (
                <Form.Group className="mb-3">
                  <Form.Label>Code</Form.Label>
                  <Form.Control type="text" value={editPromo?.code || ""} readOnly disabled />
                </Form.Group>
              )}

              <AvField
                name="discountType"
                label="Discount type *"
                type="select"
                value={isPromoEdit ? editPromo?.discountType : promoDiscountType}
                disabled={promoSubmitting}
                onChange={(e) => setPromoDiscountType(e.target.value)}
              >
                {PROMO_DISCOUNT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </AvField>

              {promoDiscountType !== "free_shipping" && (
                <AvField
                  name="discountValue"
                  label={promoDiscountType === "percentage" ? "Discount % *" : "Discount amount (INR) *"}
                  type="number"
                  min="0.01"
                  max={promoDiscountType === "percentage" ? "100" : undefined}
                  value={isPromoEdit ? editPromo?.discountValue ?? "" : ""}
                  disabled={promoSubmitting}
                  validate={{
                    required: { value: true, errorMessage: "Discount value is required" },
                  }}
                />
              )}

              <Row>
                <Col md={6}>
                  <AvField
                    name="maxUsage"
                    label="Max total usage"
                    type="number"
                    min="1"
                    value={isPromoEdit && editPromo?.maxUsage != null ? editPromo.maxUsage : ""}
                    disabled={promoSubmitting}
                    helpMessage="Leave empty for unlimited"
                  />
                </Col>
                <Col md={6}>
                  <AvField
                    name="maxUsagePerCustomer"
                    label="Max per customer"
                    type="number"
                    min="1"
                    value={isPromoEdit ? editPromo?.maxUsagePerCustomer ?? 1 : 1}
                    disabled={promoSubmitting}
                  />
                </Col>
              </Row>

              <AvField
                name="minOrderAmount"
                label="Minimum order amount (INR)"
                type="number"
                min="0"
                value={isPromoEdit && editPromo?.minOrderAmount != null ? editPromo.minOrderAmount : ""}
                disabled={promoSubmitting}
              />

              <Row>
                <Col md={6}>
                  <AvField
                    name="startDate"
                    label="Start date"
                    type="date"
                    value={isPromoEdit ? toDateInput(editPromo?.startDate) : ""}
                    disabled={promoSubmitting}
                  />
                </Col>
                <Col md={6}>
                  <AvField
                    name="endDate"
                    label="End date"
                    type="date"
                    value={isPromoEdit ? toDateInput(editPromo?.endDate) : ""}
                    disabled={promoSubmitting}
                  />
                </Col>
              </Row>

              <AvField name="firstPurchaseOnly" label="First purchase only" type="checkbox" value={isPromoEdit ? editPromo?.firstPurchaseOnly : false} disabled={promoSubmitting} />

              <AvField name="status" label="Status" type="select" value={isPromoEdit ? editPromo?.status || "active" : "active"} disabled={promoSubmitting}>
                {OFFER_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </AvField>

              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button variant="outline-secondary" onClick={() => setShowPromoModal(false)} disabled={promoSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={promoSubmitting}>
                  {promoSubmitting ? "Saving…" : isPromoEdit ? "Update" : "Create"}
                </Button>
              </div>
            </AvForm>
          </Modal.Body>
        </Modal>

        <Modal show={showOfferModal} onHide={() => setShowOfferModal(false)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{isOfferEdit ? "Edit Banner Offer" : "Create Banner Offer"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {availableCodes.length === 0 && !isOfferEdit && (
              <Alert variant="danger" className="small">
                No active codes found. <Button variant="link" className="p-0 align-baseline" onClick={() => setActiveTab("promo-codes")}>Create a platform promo code</Button> or add a referral discount code first.
              </Alert>
            )}

            <AvForm onValidSubmit={handleOfferFormSubmit}>
              <AvField
                name="title"
                label="Title *"
                type="text"
                value={isOfferEdit ? editOffer?.title ?? "" : ""}
                disabled={offerSubmitting}
                validate={{ required: { value: true, errorMessage: "Title is required" } }}
              />

              <AvField
                name="description"
                label="Description"
                type="textarea"
                value={isOfferEdit ? editOffer?.description ?? "" : ""}
                disabled={offerSubmitting}
              />

              <AvField
                name="code"
                label="Linked code *"
                type="select"
                value={isOfferEdit ? editOffer?.code ?? "" : ""}
                disabled={offerSubmitting}
                validate={{ required: { value: true, errorMessage: "Select a code" } }}
              >
                <option value="">Select code…</option>
                {availableCodes.map((item) => (
                  <option key={`${item.source}-${item.code}`} value={item.code}>
                    {item.label}
                  </option>
                ))}
                {isOfferEdit && editOffer?.code && !availableCodes.some((c) => c.code === editOffer.code) && (
                  <option value={editOffer.code}>{editOffer.code} (current)</option>
                )}
              </AvField>

              <AvField
                name="bannerText"
                label="Banner text"
                type="textarea"
                value={isOfferEdit ? editOffer?.bannerText ?? "" : ""}
                disabled={offerSubmitting}
                onChange={(e) => setOfferBannerPreview(e.target.value)}
                helpMessage="Shown on storefront. Defaults to auto-generated text if empty."
              />

              <div className="mb-3 p-3 rounded border bg-light">
                <Form.Label className="small text-muted mb-1">Banner preview</Form.Label>
                <div className="fw-semibold">
                  {offerBannerPreview ||
                    (isOfferEdit ? editOffer?.bannerText : "") ||
                    "✨ [Title] - Use code: [CODE]"}
                </div>
              </div>

              <Row>
                <Col md={6}>
                  <AvField name="offerType" label="Offer type" type="select" value={isOfferEdit ? editOffer?.offerType ?? "" : ""} disabled={offerSubmitting}>
                    <option value="">Auto-detect from code</option>
                    {OFFER_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </AvField>
                </Col>
                <Col md={6}>
                  <AvField name="priority" label="Priority" type="number" value={isOfferEdit ? editOffer?.priority ?? 0 : 0} disabled={offerSubmitting} helpMessage="Higher = shown first" />
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <AvField name="startDate" label="Start date" type="date" value={isOfferEdit ? toDateInput(editOffer?.startDate) : ""} disabled={offerSubmitting} />
                </Col>
                <Col md={6}>
                  <AvField name="endDate" label="End date" type="date" value={isOfferEdit ? toDateInput(editOffer?.endDate) : ""} disabled={offerSubmitting} />
                </Col>
              </Row>

              <AvField name="badgeText" label="Badge text" type="text" value={isOfferEdit ? editOffer?.badgeText ?? "" : ""} disabled={offerSubmitting} placeholder="e.g. NEW" />

              <AvField name="showCountdown" label="Show countdown" type="checkbox" value={isOfferEdit ? editOffer?.showCountdown : false} disabled={offerSubmitting} />

              <AvField name="status" label="Status" type="select" value={isOfferEdit ? editOffer?.status || "active" : "active"} disabled={offerSubmitting}>
                {OFFER_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </AvField>

              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button variant="outline-secondary" onClick={() => setShowOfferModal(false)} disabled={offerSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={offerSubmitting}>
                  {offerSubmitting ? "Saving…" : isOfferEdit ? "Update" : "Create"}
                </Button>
              </div>
            </AvForm>
          </Modal.Body>
        </Modal>

        <Modal show={Boolean(viewOffer)} onHide={() => setViewOffer(null)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Offer details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {viewOfferLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
              </div>
            ) : viewOffer ? (
              <Table bordered responsive className="mb-0">
                <tbody>
                  <tr><th style={{ width: "180px" }}>Title</th><td>{safeText(viewOffer.title)}</td></tr>
                  <tr><th>Banner text</th><td>{safeText(viewOffer.bannerText)}</td></tr>
                  <tr><th>Code</th><td><strong>{safeText(viewOffer.code)}</strong></td></tr>
                  <tr><th>Code source</th><td>{safeText(viewOffer.codeSource)}</td></tr>
                  <tr><th>Offer type</th><td>{safeText(viewOffer.offerType)}</td></tr>
                  <tr><th>Priority</th><td>{viewOffer.priority ?? 0}</td></tr>
                  <tr><th>Status</th><td><Badge bg={getOfferStatusBadge(viewOffer.status)}>{viewOffer.status}</Badge></td></tr>
                  <tr><th>Views / Clicks / Copies</th><td>{viewOffer.viewCount ?? 0} / {viewOffer.clickCount ?? 0} / {viewOffer.copyCount ?? 0}</td></tr>
                  <tr><th>Schedule</th><td>{viewOffer.startDate ? moment(viewOffer.startDate).format("DD MMM YYYY") : "—"} → {viewOffer.endDate ? moment(viewOffer.endDate).format("DD MMM YYYY") : "—"}</td></tr>
                  <tr><th>Created</th><td>{viewOffer.createdAt ? moment(viewOffer.createdAt).format("DD MMM YYYY, hh:mm A") : "—"}</td></tr>
                </tbody>
              </Table>
            ) : null}
          </Modal.Body>
        </Modal>

        <ConfirmModal
          show={confirm}
          onCloseClick={() => {
            if (!actionLoading) {
              setConfirm(false);
              setCurrentData(null);
            }
          }}
          onConfirm={handleConfirmDelete}
          data={currentData}
        />
      </div>
      <Footer />
    </React.Fragment>
  );
}
